import React, { createContext, useContext, useEffect, useState } from 'react';

interface User {
  email: string;
  id: string;
  onboarding_completed: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string) => Promise<{ error?: any; success?: boolean; onboarding_completed?: boolean }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const validateSession = async () => {
    try {
      const res = await fetch('/api/auth/validate', { credentials: 'include' });
      const data = await res.json();

      if (data.authenticated) {
        setUser({
          email: data.user.email,
          id: data.user.id,
          onboarding_completed: data.user.onboarding_completed
        });
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Session validation error:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    validateSession();
  }, []);

  const signIn = async (email: string) => {
    try {
      const res = await fetch('/api/auth/direct-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include'
      });

      const data = await res.json();

      if (!res.ok) {
        return { error: new Error(data.error || 'Access denied') };
      }

      setUser(data.user);
      return { success: true, onboarding_completed: data.user.onboarding_completed };
    } catch (err: any) {
      console.error("DEBUG AUTH: signIn error", err);
      return { error: err };
    }
  };

  const signOut = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      setUser(null);
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const refreshUser = async () => {
    await validateSession();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};